import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class IdGeneratorService {
  
  /**
   * Generates a 10-character alphanumeric string for use as temporary IDs
   * Uses a combination of current timestamp and random characters for uniqueness
   * @returns {string} A 10-character alphanumeric ID (e.g., "A1B2C3D4E5")
   */
  generateAlphanumericId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    
    // Use timestamp for first part to ensure some uniqueness
    const timestamp = Date.now().toString(36); // Convert to base36 for shorter string
    
    // Take last 4 characters from timestamp
    const timestampPart = timestamp.slice(-4).toUpperCase();
    result += timestampPart;
    
    // Generate remaining 6 characters randomly
    for (let i = result.length; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return result;
  }

  /**
   * Generates multiple unique alphanumeric IDs
   * @param count Number of IDs to generate
   * @returns {string[]} Array of unique alphanumeric IDs
   */
  generateMultipleIds(count: number): string[] {
    const ids = new Set<string>();
    
    while (ids.size < count) {
      ids.add(this.generateAlphanumericId());
    }
    
    return Array.from(ids);
  }

  /**
   * Validates if a string is a valid 10-character alphanumeric ID
   * @param id The ID to validate
   * @returns {boolean} True if valid, false otherwise
   */
  isValidAlphanumericId(id: string): boolean {
    if (!id || typeof id !== 'string' || id.length !== 10) {
      return false;
    }
    
    const alphanumericRegex = /^[A-Za-z0-9]{10}$/;
    return alphanumericRegex.test(id);
  }
}